// FILE: AgentAvatar.logic.ts
// Purpose: Build managed-agent avatar URLs and fallback presentation values.
// Exports: `agentAvatarSrc`, `agentInitials`, `agentAvatarFallbackStyle`.

import { subagentAccentColor } from "~/lib/subagentPresentation";
import { initials } from "~/lib/initials";

export { initials as agentInitials };

/**
 * URL of a managed agent's avatar, or `null` when it has no stored file.
 *
 * The versioned file name is only a cache key here. The server resolves the
 * current file from the agent row and serves it immutably, just like user
 * avatars do.
 */
export function agentAvatarSrc(input: {
  readonly agentId: string;
  readonly avatarFileName: string | null | undefined;
  readonly origin?: string;
}): string | null {
  const fileName = input.avatarFileName?.trim();
  if (!fileName) return null;

  const path = `/api/agents/${encodeURIComponent(input.agentId)}/avatar`;
  const query = `?v=${encodeURIComponent(fileName)}`;
  const origin = input.origin?.trim() ?? "";
  return origin.length > 0 ? `${origin.replace(/\/+$/, "")}${path}${query}` : `${path}${query}`;
}

/**
 * Fallback colour for an agent without a picture.
 *
 * `accentColor` is accepted when the caller already resolved a subagent
 * presentation. Otherwise the shared subagent palette is used directly, so
 * nickname colours stay identical across sidebar/chat surfaces.
 */
export function agentAvatarFallbackStyle(
  seed: string | null | undefined,
  accentColor?: string | null | undefined,
): { readonly backgroundColor: string; readonly color: string } {
  const backgroundColor = accentColor?.trim() || subagentAccentColor(seed);
  return { backgroundColor, color: "hsl(0 0% 100%)" };
}
